import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper, CircularProgress } from '@mui/material';
import { Close, AccountBalanceWallet, SupervisorAccount } from '@mui/icons-material';
import { api } from '../../../services/api';
import RendirEfectivoModal from './RendirEfectivoModal';
import { toast } from 'react-toastify';

const UsuariosSaldos = ({ open, onClose, eventoId }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openRendirModal, setOpenRendirModal] = useState(false);
  const [usuarioParaRendir, setUsuarioParaRendir] = useState(null);
  const [loadingRendir, setLoadingRendir] = useState(false);

  useEffect(() => {
    if (open && eventoId) {
      fetchUsuarios();
    }
  }, [open, eventoId]);

  const fetchUsuarios = async () => {
    setLoading(true);
    const res = await api.rendiciones.getUsuariosSaldos(eventoId);
    if (res.status === "success") {
      setUsuarios(res.data || []);
    } else {
      toast.error("Error al cargar tesoreros");
    }
    setLoading(false);
  };

  const handleRendirUsuario = async (data) => {
    if (!usuarioParaRendir) return;

    setLoadingRendir(true);
    const res = await api.rendiciones.rendirEfectivo(eventoId, {
      vendedor_id: usuarioParaRendir.idusuario,
      monto_efectivo: data.monto_efectivo,
      observaciones: data.observaciones
    });
    setLoadingRendir(false);

    if (res.status === "success") {
      setOpenRendirModal(false);
      setUsuarioParaRendir(null);
      toast.success("Rendición de tesorero procesada correctamente");
      fetchUsuarios();
    } else {
      toast.error(res.message || "Error al procesar rendición");
    }
  };

  const abrirModalRendir = (usuario) => {
    setUsuarioParaRendir({
      ...usuario,
      saldo_caja: usuario.saldo_caja,
      caja: `${usuario.rol} - ${usuario.usuario}`,
      nombre_completo: usuario.nombre_completo,
      vendedor_id: usuario.idusuario
    });
    setOpenRendirModal(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <SupervisorAccount sx={{ mr: 1, color: "#5656FF" }} />
              <Typography variant="h6" sx={{
                fontFamily: "Bricolage Grotesque",
                color: "#0b0062",
                fontWeight: 600
              }}>
                Usuarios con Saldos
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2, color: "#666", fontSize: "14px" }}>
            Aquí podés ver todos los usuarios que tienen efectivo y no son vendedores.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Cargando usuarios...</Typography>
            </Box>
          ) : usuarios.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 4,
              backgroundColor: "#f9f9f9",
              borderRadius: 2
            }}>
              <SupervisorAccount sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Typography color="text.secondary">
                No hay usuarios con saldos en efectivo
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
                  <TableRow sx={{ borderColor: "#E6E6FF", textAlign: "center" }}>
                    <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Usuario</TableCell>
                    <TableCell align="center" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }} >Rol</TableCell>
                    <TableCell align="right" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Saldo Efectivo</TableCell>
                    <TableCell align="center" sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.idusuario} hover>
                      <TableCell>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: "14px", fontFamily: "Bricolage Grotesque" }}>
                            {usuario.nombre_completo}
                          </Typography>
                          <Typography sx={{ color: "#666", fontSize: "12px", fontFamily: "Bricolage Grotesque" }}>
                            {usuario.usuario}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={usuario.rol}
                          size="small"
                          sx={{
                            backgroundColor: usuario.rol === 'Owner' ? "#e8f5e8" : "#e3f2fd",
                            color: usuario.rol === 'Owner' ? "#2e7d32" : "#1976d2",
                            fontWeight: 500,
                            fontSize: "11px"
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <AccountBalanceWallet sx={{ fontSize: 16, mr: 0.5, color: "#2e7d32" }} />
                          <Typography sx={{
                            fontWeight: 600,
                            color: "#2e7d32",
                            fontSize: "14px"
                          }}>
                            ${usuario.saldo_caja.toLocaleString('es-AR')}
                          </Typography>
                        </Box>
                        {usuario.total_ventas > 0 && (
                          <Typography sx={{ color: "#666", fontSize: "11px" }}>
                            {usuario.total_ventas} transacciones
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => abrirModalRendir(usuario)}
                          sx={{
                            color: "#5656ff",
                            borderColor: "#5656ff",
                            fontSize: "11px",
                            minWidth: "60px",
                            height: "28px",
                            '&:hover': {
                              borderColor: "#5656ff",
                              backgroundColor: "#e8e8ff"
                            }
                          }}
                        >
                          Rendir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <RendirEfectivoModal
        open={openRendirModal}
        onClose={() => {
          setOpenRendirModal(false);
          setUsuarioParaRendir(null);
        }}
        onRendir={handleRendirUsuario}
        caja={usuarioParaRendir}
        loading={loadingRendir}
      />
    </>
  );
};

export default UsuariosSaldos;